import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApi, type Session } from "../lib/api.ts";
import { useAsync } from "../lib/useAsync.ts";
import { formatBytes, formatRelative } from "../lib/format.ts";
import { CopyField } from "../components/CopyField.tsx";
import {
  EmptyState,
  ErrorState,
  PageHeader,
  Spinner,
} from "../components/States.tsx";

export default function Sessions() {
  const api = useApi();
  const fetcher = useCallback(
    (signal: AbortSignal) => api.listSessions(signal),
    [api],
  );
  const { data, error, loading, reload } = useAsync(fetcher, [api]);
  const [query, setQuery] = useState("");

  const sessions = useMemo(() => {
    const items = data ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((s) =>
      [s.agent, s.host, s.project, s.title].some((f) =>
        f?.toLowerCase().includes(q),
      ),
    );
  }, [data, query]);

  return (
    <section>
      <PageHeader
        title="Sessions"
        subtitle="Coding-agent sessions synced to this organization."
        actions={
          data && data.length > 0 ? (
            <input
              className="input input--search"
              type="search"
              placeholder="Filter by agent, host, project…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          ) : null
        }
      />

      {loading && <Spinner />}
      {error && <ErrorState error={error} onRetry={reload} />}

      {data && data.length === 0 && <SessionsEmpty />}

      {data && data.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Agent</th>
                <th>Host</th>
                <th>Project</th>
                <th>Title</th>
                <th className="num">Size</th>
                <th className="num">Updated</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <SessionRow key={s.id} session={s} />
              ))}
              {sessions.length === 0 && (
                <tr>
                  <td colSpan={7} className="table-empty">
                    No sessions match “{query}”.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function SessionRow({ session }: { session: Session }) {
  const api = useApi();
  const [downloading, setDownloading] = useState(false);

  async function download() {
    if (downloading) return;
    setDownloading(true);
    try {
      const url = await api.sessionArchiveUrl(session.id);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${session.id}.tar.gz`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoke after the navigation has had a chance to start.
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch {
      // TODO: surface a toast on failure.
    } finally {
      setDownloading(false);
    }
  }

  return (
    <tr>
      <td>
        <span className="badge badge--agent">{session.agent}</span>
      </td>
      <td className="mono dim">{session.host}</td>
      <td className="mono">{session.project}</td>
      <td className="ellipsis" title={session.title}>
        {session.title || <span className="dim">untitled</span>}
      </td>
      <td className="num mono dim">{formatBytes(session.size)}</td>
      <td className="num dim" title={session.updated_at}>
        {formatRelative(session.updated_at)}
      </td>
      <td className="num">
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={download}
          disabled={downloading}
        >
          {downloading ? "…" : "Download"}
        </button>
      </td>
    </tr>
  );
}

function SessionsEmpty() {
  return (
    <EmptyState title="No sessions yet">
      <p>
        Once you push from a coding agent, sessions show up here. Connect the
        CLI to this org:
      </p>
      <CopyField
        value="stift login https://sync.stift.sh --token <token>"
        prompt="$"
        multiline
      />
      <p className="dim">
        Need a token? Create one on the{" "}
        <Link to="/tokens" className="link">
          Tokens
        </Link>{" "}
        page, then push with <code className="inline-code">stift push</code>.
      </p>
    </EmptyState>
  );
}
